import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, MessageCircle, Menu, X, Languages, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { servicesData } from '../data/services';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const toggleLanguage = () => {
    const newLang = i18n.language === 'en' ? 'hi' : 'en';
    i18n.changeLanguage(newLang);
  };

  const navLinks = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.services'), path: '/services', hasDropdown: true },
    { name: t('nav.enquiry'), path: '/enquiry' },
    { name: t('nav.contact'), path: '/contact' },
  ];

  return (
    <header className="bg-white sticky top-0 z-50 shadow-sm border-b border-gold-primary/20">
      <div className="container mx-auto px-4 py-3">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex flex-col">
            <h1 className="text-2xl font-bold text-dark-green tracking-tight leading-none">VSK ASSOCIATES</h1>
            <p className="text-[10px] text-gold-primary font-semibold tracking-[0.2em] mt-1 uppercase">Your Trust, Our Commitment</p>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center space-x-6">
            {navLinks.map((link) => (
              <div 
                key={link.path} 
                className="relative group"
                onMouseEnter={() => link.hasDropdown && setIsServicesOpen(true)}
                onMouseLeave={() => link.hasDropdown && setIsServicesOpen(false)}
              >
                <Link
                  to={link.path}
                  className={`text-sm font-medium transition-colors hover:text-gold-primary flex items-center gap-1 py-4 ${
                    location.pathname.startsWith(link.path) && link.path !== '/' ? 'text-gold-primary' : 'text-dark-green'
                  }`}
                >
                  {link.name}
                  {link.hasDropdown && <ChevronDown size={14} className={`transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`} />}
                </Link>

                {link.hasDropdown && (
                  <div className={`absolute top-full left-0 w-64 bg-white border border-gold-primary/20 shadow-2xl rounded-b-2xl transition-all duration-300 origin-top ${
                    isServicesOpen ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0 pointer-events-none'
                  }`}>
                    <div className="py-2">
                      {Object.keys(servicesData).map((key) => (
                        <Link
                          key={key}
                          to={`/services/${key}`}
                          className="block px-6 py-3 text-sm text-dark-green hover:bg-gold-primary/10 hover:text-gold-primary transition-colors font-medium border-l-4 border-transparent hover:border-gold-primary"
                        >
                          {t(`services.categories.${key}.title`)}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
            
            {/* Language Switcher */}
            <button 
              onClick={toggleLanguage}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-cream-off border border-gold-primary/30 rounded-full text-xs font-bold text-dark-green hover:bg-gold-primary/10 transition-colors"
            >
              <Languages size={14} className="text-gold-primary" />
              {i18n.language === 'en' ? 'हिन्दी' : 'English'}
            </button>

            <div className="flex items-center space-x-3">
              <a href="tel:8936835037" className="p-2 bg-dark-green text-white rounded-full hover:bg-dark-green-deep transition-colors">
                <Phone size={18} />
              </a>
              <a href="https://wa.me/918936835037" className="p-2 bg-green-whatsapp text-white rounded-full hover:opacity-90 transition-opacity">
                <MessageCircle size={18} />
              </a>
            </div>
          </nav>

          {/* Mobile Actions (Always visible) */}
          <div className="flex items-center space-x-4 md:hidden">
            <button 
              onClick={toggleLanguage}
              className="flex items-center gap-1 px-2.5 py-1.5 bg-cream-off border border-gold-primary/30 rounded-full text-[10px] font-bold text-dark-green"
            >
              {i18n.language === 'en' ? 'हिन्दी' : 'English'}
            </button>
            <button className="text-dark-green" onClick={() => setIsOpen(!isOpen)}>
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {isOpen && (
          <nav className="md:hidden mt-4 pb-4 flex flex-col space-y-2 animate-in fade-in slide-in-from-top-4 duration-300 max-h-[80vh] overflow-y-auto">
            {navLinks.map((link) => (
              <div key={link.path}>
                <div className="flex justify-between items-center py-2">
                  <Link
                    to={link.path}
                    className={`text-base font-semibold ${
                      location.pathname === link.path ? 'text-gold-primary' : 'text-dark-green'
                    }`}
                    onClick={() => !link.hasDropdown && setIsOpen(false)}
                  >
                    {link.name}
                  </Link>
                  {link.hasDropdown && (
                    <button 
                      onClick={() => setIsServicesOpen(!isServicesOpen)}
                      className="p-2 text-gold-primary bg-gold-primary/5 rounded-lg"
                    >
                      <ChevronDown size={20} className={`transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>
                
                {link.hasDropdown && isServicesOpen && (
                  <div className="pl-4 space-y-2 mb-4 animate-in slide-in-from-left-2 duration-300">
                    {Object.keys(servicesData).map((key) => (
                      <Link
                        key={key}
                        to={`/services/${key}`}
                        className="block py-2 text-sm text-gray-600 font-medium border-l-2 border-gold-primary/20 pl-3"
                        onClick={() => setIsOpen(false)}
                      >
                        {t(`services.categories.${key}.title`)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="flex flex-col space-y-3 pt-4 border-t border-gray-100">
              <a href="tel:8936835037" className="flex items-center justify-center space-x-2 bg-dark-green text-white py-3 rounded-lg font-bold">
                <Phone size={20} />
                <span>{t('contact.callNow')}</span>
              </a>
              <a href="https://wa.me/918936835037" className="flex items-center justify-center space-x-2 bg-green-whatsapp text-white py-3 rounded-lg font-bold">
                <MessageCircle size={20} />
                <span>{t('contact.whatsapp')}</span>
              </a>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
